/* @jsx h */
import { defineComponent, h, ref, computed, PropType } from "vue";
import { RESTAURANT_FEATURES } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";

export const RestaurantFilters = defineComponent({
  name: "RestaurantFilters",
  props: {
    selectedFeatures: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    minGoogleRating: {
      type: Number,
      default: 0,
    },
    hasGoogleRatings: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:selectedFeatures", "update:minGoogleRating"],
  setup(props, { emit }) {
    const { t } = useLocalization();
    const showAll = ref(false);
    const mobileExpanded = ref(false);

    const visibleFeatures = computed(() => {
      return showAll.value
        ? RESTAURANT_FEATURES
        : RESTAURANT_FEATURES.slice(0, 10);
    });

    const toggleFeature = (slug: string) => {
      const current = [...props.selectedFeatures];
      const idx = current.indexOf(slug);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(slug);
      }
      emit("update:selectedFeatures", current);
    };

    const clearAll = () => {
      emit("update:selectedFeatures", []);
      emit("update:minGoogleRating", 0);
    };

    const hasActiveFilters = computed(() => {
      return props.selectedFeatures.length > 0 || props.minGoogleRating > 0;
    });

    const ratingSteps = [0, 3, 3.5, 4, 4.5];

    return () => (
      <div class="mb-8">
        {/* Mobile toggle button */}
        <button
          type="button"
          onClick={() => (mobileExpanded.value = !mobileExpanded.value)}
          class="md:hidden flex items-center gap-2 px-4 py-2.5 mb-3 rounded-lg text-sm font-light text-gray-600 border border-gray-200 bg-white w-full justify-between"
          style="transition: border-color 200ms ease;"
        >
          <span class="flex items-center gap-2">
            <span
              class="material-symbols-outlined text-base"
              aria-hidden="true"
            >
              tune
            </span>
            {t("customer.home.filter_restaurants")}
            {hasActiveFilters.value && (
              <span
                class="inline-flex items-center justify-center w-5 h-5 text-xs text-white rounded-full"
                style="background-color: var(--accent);"
              >
                {props.selectedFeatures.length +
                  (props.minGoogleRating > 0 ? 1 : 0)}
              </span>
            )}
          </span>
          <span class="material-symbols-outlined text-base" aria-hidden="true">
            {mobileExpanded.value ? "expand_less" : "expand_more"}
          </span>
        </button>

        {/* Feature chips — always visible on desktop, toggled on mobile */}
        <div class={`${mobileExpanded.value ? "" : "hidden"} md:block`}>
          <div class="flex flex-wrap gap-2 items-center">
            {visibleFeatures.value.map((feature) => {
              const isSelected = props.selectedFeatures.includes(feature.slug);
              return (
                <button
                  key={feature.slug}
                  type="button"
                  onClick={() => toggleFeature(feature.slug)}
                  aria-pressed={isSelected}
                  class={`inline-flex items-center gap-1.5 px-4 min-h-[44px] rounded-full text-sm border whitespace-nowrap ${
                    isSelected
                      ? "border-transparent text-white shadow-sm"
                      : "border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-900 bg-white"
                  }`}
                  style={{
                    ...(isSelected
                      ? {
                          backgroundColor: "var(--accent)",
                          color: "#fff",
                        }
                      : {}),
                    transition:
                      "background-color 200ms ease, color 200ms ease, border-color 200ms ease, box-shadow 200ms ease",
                  }}
                >
                  <span
                    class="material-symbols-outlined text-sm"
                    aria-hidden="true"
                  >
                    {feature.icon}
                  </span>
                  <span class="font-light">{feature.name}</span>
                </button>
              );
            })}

            {!showAll.value && RESTAURANT_FEATURES.length > 10 && (
              <button
                type="button"
                onClick={() => (showAll.value = true)}
                class="inline-flex items-center gap-1 px-4 min-h-[44px] rounded-full text-sm border border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-400 bg-white"
                style="transition: color 200ms ease, border-color 200ms ease;"
              >
                <span
                  class="material-symbols-outlined text-sm"
                  aria-hidden="true"
                >
                  expand_more
                </span>
                <span class="font-light">
                  {t("customer.home.more_filters", [
                    RESTAURANT_FEATURES.length - 10,
                  ])}
                </span>
              </button>
            )}

            {showAll.value && (
              <button
                type="button"
                onClick={() => (showAll.value = false)}
                class="inline-flex items-center gap-1 px-4 min-h-[44px] rounded-full text-sm border border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-400 bg-white"
                style="transition: color 200ms ease, border-color 200ms ease;"
              >
                <span
                  class="material-symbols-outlined text-sm"
                  aria-hidden="true"
                >
                  expand_less
                </span>
                <span class="font-light">{t("customer.home.show_less")}</span>
              </button>
            )}
          </div>

          {/* Google rating slider + clear */}
          <div class="flex items-center gap-4 mt-3">
            {props.hasGoogleRatings && (
              <div class="flex items-center gap-2">
                <span
                  class="material-symbols-outlined text-sm text-yellow-500"
                  aria-hidden="true"
                >
                  star
                </span>
                <span class="text-xs text-gray-500 font-light whitespace-nowrap">
                  {t("customer.home.google_rating")}
                </span>
                <div class="flex gap-1">
                  {ratingSteps.map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => emit("update:minGoogleRating", rating)}
                      class={`px-3 min-h-[36px] rounded-lg text-xs font-medium ${
                        props.minGoogleRating === rating
                          ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                          : "bg-gray-50 text-gray-500 border border-gray-200 hover:border-gray-300"
                      }`}
                      style="transition: background-color 200ms ease, border-color 200ms ease;"
                    >
                      {rating === 0 ? t("customer.home.any") : `${rating}+`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {hasActiveFilters.value && (
              <button
                type="button"
                onClick={clearAll}
                class="text-xs text-gray-400 hover:text-gray-600 font-light underline ml-auto"
                style="transition: color 200ms ease;"
              >
                {t("customer.home.clear_filters")}
              </button>
            )}
          </div>
        </div>
        {/* end collapsible wrapper */}
      </div>
    );
  },
});
